import { Link } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';

/**
 * Top-of-app banner for admins: lists every external invitee who has signed
 * up and is ready for their family-key handoff. One row per finalization,
 * each linking to the family's Manage tab where the `Finalize` button lives.
 *
 * Source of truth is `SafeContext.pendingFinalizations`, refreshed on sign-in,
 * unlock, refreshOrgs, and after a successful finalize. The banner is not
 * dismissible — it's an actionable signal and the only way to clear it is to
 * finalize (or revoke) the invite.
 */
export function PendingFinalizationsBanner(): JSX.Element | null {
  const { pendingFinalizations } = useSafe();
  if (pendingFinalizations.length === 0) return null;

  return (
    <ul aria-label="Pending family finalizations" className="flex flex-col">
      {pendingFinalizations.map((f) => (
        <li
          key={`${f.orgId}:${f.token}`}
          className="bg-rose-50 border-b border-rose-200 px-4 py-2 flex items-center justify-between gap-4"
        >
          <span className="text-sm text-rose-900">
            <span
              aria-hidden="true"
              className="inline-block w-2 h-2 rounded-full bg-hbs-danger mr-2 align-middle"
            />
            <strong>{f.invitedEmail}</strong> has created an account. Go to the{' '}
            <Link
              to={`/orgs/${f.orgId}/manage`}
              className="font-semibold underline hover:no-underline"
            >
              {f.orgName} manage page
            </Link>{' '}
            to add them.
          </span>
        </li>
      ))}
    </ul>
  );
}
